import { useEffect, useRef, useState } from 'react'

/* ------------------------------------------------------------------
   حرکتِ ورود و خروج — یک قاعده برای همهٔ لایه‌های شناور
   ------------------------------------------------------------------
   🔴 ۱۴۰۵/۰۸/۱۳ «از باز شدنِ صفحه تا لود شدنِ متن‌ها… حسِ smooth بودن رو
   اصلاً به کاربر نمی‌ده». ضبطِ فریم‌به‌فریم نشان داد دیالوگ، جست‌وجو و منو
   در **یک فریم** ظاهر و در یک فریم ناپدید می‌شدند (۸۳٪ ِ صفحه یک‌باره عوض
   می‌شد). علتِ مشترک: `if (!open) return null` — عنصری که همان لحظه از DOM
   بیرون می‌رود دیگر فرصتِ انیمیشنِ خروج ندارد.

   `usePresence` عنصر را پس از بسته شدن به اندازهٔ خروج نگه می‌دارد و
   `data-state` را `closed` می‌کند تا CSS (`.pf-pop`، `.pf-scrim`، … در
   tokens.css) آن را بیرون ببرد؛ بعد واقعاً برمی‌دارد.

   ⚠️ مدت همان توکنِ ‎--pf-duration-exit‎ در tokens.css است؛ اگر یکی را
   عوض کردید، دیگری را هم.
   ------------------------------------------------------------------ */

export const EXIT_MS = 140

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
}

export function usePresence(open: boolean, exitMs: number = EXIT_MS) {
  /* lingering = «بسته شده ولی هنوز در حالِ بیرون رفتن» */
  const [lingering, setLingering] = useState(false)
  const [prevOpen, setPrevOpen] = useState(open)

  /*
    ⚠️ همین‌جا در رندر، نه در افکت: اگر lingering در افکت روشن می‌شد، رندرِ
    همان لحظهٔ بسته شدن `mounted = false` می‌داد — عنصر یک بار برداشته و
    دوباره ساخته می‌شد و انیمیشنِ خروج روی عنصرِ تازه (با فوکوس و اسکرولِ
    از دست رفته) پخش می‌شد. الگوی «state از رندرِ قبلی» ِ خودِ React است.
  */
  if (prevOpen !== open) {
    setPrevOpen(open)
    setLingering(!open && exitMs > 0 && !prefersReducedMotion())
  }

  useEffect(() => {
    if (!lingering) return
    const timer = window.setTimeout(() => setLingering(false), exitMs)
    return () => window.clearTimeout(timer)
  }, [lingering, exitMs])

  return {
    /** باید رندر شود؟ */
    mounted: open || lingering,
    /** برای `data-state` */
    state: (open ? 'open' : 'closed') as 'open' | 'closed',
    closing: !open && lingering,
  }
}

/*
  🔴 محتوای آخرین لحظهٔ باز بودن، در طولِ خروج.

  ⚠️ بیشترِ فراخوان‌ها محتوا را از همان state ای می‌سازند که `open` را
  می‌سازد: `open={!!editing}` و درونش `editing.name`. با بسته شدن، editing
  null می‌شود ولی دیالوگ هنوز ۱۴۰ میلی‌ثانیه دیده می‌شود — بی این، یا محتوا
  وسطِ خروج خالی می‌شد یا رندر با `null` می‌شکست.
*/
export function useHeldWhileClosing<T>(value: T, open: boolean): T {
  const held = useRef(value)
  if (open) held.current = value
  return open ? value : held.current
}
